import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { usePagination, SearchBar } from '../components/Pagination';
import { exportPaiementsCSV } from '../utils/export.helpers';
import { formatXof } from '../utils/format';

export default function GestionPaiements() {
  const [rows, setRows]       = useState([]);
  const [classes, setClasses] = useState([]);
  const [years, setYears]     = useState([]);
  const [filters, setFilters] = useState({ classId: '', status: '', yearId: '' });
  const [error, setError]     = useState('');
  const [modal, setModal]     = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/catalog/classes').then(setClasses).catch(() => {});
    api.get('/catalog/years').then(setYears).catch(() => {});
    load();
  }, []);

  function load() {
    api.get('/finance/unpaid').then((unpaid) => {
      api.get('/students').then((students) => {
        const map = new Map((unpaid || []).map((u) => [u.studentId, u]));
        const allRows = (students || []).map((s) => {
          const u = map.get(s.id);
          return {
            id: s.id,
            numero: s.matricule,
            nom: `${s.lastName} ${s.firstName}`,
            classe: s.enrollments?.[0]?.class?.name || '—',
            montantAssigne: u?.totalDu || 0,
            totalRegle: u?.totalPaye || 0,
            resteAPayer: u?.balance || 0,
            pctPaye: u?.totalDu > 0 ? Math.round((u.totalPaye / u.totalDu) * 100) : 0,
            statut: !u ? 'Non assigné' : u.balance === 0 ? 'Soldé' : u.totalPaye > 0 ? 'En cours' : 'Non commencé',
          };
        });
        setRows(allRows);
      });
    }).catch((e) => setError(e.message));
  }

  const filtered = rows.filter((r) => {
    if (filters.classId && !r.classe.includes(classes.find((c) => c.id === filters.classId)?.name || '')) return false;
    if (filters.status && r.statut !== filters.status) return false;
    return true;
  });

  const fraisAttendus = rows.reduce((s, r) => s + r.montantAssigne, 0);
  const totalEncaisse = rows.reduce((s, r) => s + r.totalRegle, 0);
  const soldeRestant  = rows.reduce((s, r) => s + r.resteAPayer, 0);
  const tauxRecouvrement = fraisAttendus > 0 ? Math.round((totalEncaisse / fraisAttendus) * 100) : 0;

  const STATUS_COLOR = { 'Soldé': 'green', 'En cours': 'gold', 'Non commencé': 'red', 'Non assigné': 'gray' };


  const { paginated: paiesPaginated, search: paiesSearch, setSearch: setPaiesSearch, Pagination: PaiesPagination, total: paiesTotal, filtered: paiesFiltered } = usePagination(filtered, { pageSize: 5, searchKeys: ['nom', 'numero', 'classe', 'statut'] });
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: '1.2rem', fontWeight: 600 }}>Gestion des Paiements</h1>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Supervisez le recouvrement des frais, gérez les tarifs et enregistrez les versements.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost btn-sm" onClick={() => exportPaiementsCSV(filtered)}>↓ Exporter Excel</button>
          <button className="btn-ghost btn-sm">Tarifs de référence</button>
          <button className="btn-ghost btn-sm">Journal d'audit</button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <div className="card">
          <div className="card-body" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>📋 FRAIS ATTENDUS</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, fontFamily: 'monospace' }}>{formatXof(fraisAttendus)}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>✅ TOTAL ENCAISSÉ</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, fontFamily: 'monospace', color: 'var(--success)' }}>{formatXof(totalEncaisse)}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>⏳ SOLDE RESTANT</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, fontFamily: 'monospace', color: soldeRestant > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatXof(soldeRestant)}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>📈 TAUX DE RECOUVREMENT</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: tauxRecouvrement >= 80 ? 'var(--success)' : tauxRecouvrement >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
              {tauxRecouvrement} %
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, marginBottom: 16, alignItems: 'flex-end' }}>
        <div className="field" style={{ margin: 0 }}><label>Classe</label>
          <select value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
            <option value="">Toutes les classes</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}><label>Statut de paiement</label>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Tous les statuts</option>
            <option value="Soldé">Soldé</option>
            <option value="En cours">En cours</option>
            <option value="Non commencé">Non commencé</option>
            <option value="Non assigné">Non assigné</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}><label>Année académique</label>
          <select value={filters.yearId} onChange={(e) => setFilters({ ...filters, yearId: e.target.value })}>
            <option value="">Toutes les années</option>
            {years.map((y) => <option key={y.id} value={y.id}>{y.label}{y.isActive ? ' (en cours)' : ' (Clôturée)'}</option>)}
          </select>
        </div>
        <button className="btn btn-sm" onClick={load} style={{ height: 38 }}>🔍 Filtrer</button>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <SearchBar value={paiesSearch} onChange={setPaiesSearch} placeholder="Nom, matricule, classe…" total={paiesTotal} filtered={paiesFiltered.length} />
          {paiesFiltered.length === 0 ? <div className="empty">Aucun résultat.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Numéro</th><th>Étudiant</th><th>Classe</th>
                  <th className="num">Montant assigné</th>
                  <th className="num">Total réglé</th>
                  <th className="num">Reste à payer</th>
                  <th style={{ width: 140 }}>% payé</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>{paiesPaginated.map((r) => (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize: 11 }}>{r.numero}</td>
                  <td><b>{r.nom}</b></td>
                  <td>{r.classe}</td>
                  <td className="num mono">{r.montantAssigne > 0 ? formatXof(r.montantAssigne) : <span style={{ color: 'var(--muted)' }}>Non assigné</span>}</td>
                  <td className="num mono" style={{ color: r.totalRegle > 0 ? 'var(--success)' : 'inherit' }}>{formatXof(r.totalRegle)}</td>
                  <td className="num mono" style={{ color: r.resteAPayer > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatXof(r.resteAPayer)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${r.pctPaye}%`, background: r.pctPaye === 100 ? 'var(--success)' : r.pctPaye > 50 ? 'var(--warn)' : 'var(--danger)', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 11, minWidth: 30 }}>{r.pctPaye}%</span>
                    </div>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, color: r.statut === 'Soldé' ? 'var(--success)' : r.statut === 'En cours' ? 'var(--warn)' : 'var(--danger)' }}>
                      {r.statut === 'Soldé' ? '●' : r.statut === 'En cours' ? '●' : '●'} {r.statut}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sm" style={{ background: 'var(--navy)', border: 'none', fontSize: 11 }}
                      onClick={() => setModal(r)}>
                      Voir Détails ›
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && <PaiementDetailModal student={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </>
  );
}

function PaiementDetailModal({ student, onClose, onSaved }) {
  const [invoices, setInvoices] = useState([]);
  const [payForm, setPayForm]   = useState({ amountXof: '', method: 'ESPECES', reference: '' });
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    api.get(`/finance/students/${student.id}/situation`).then((s) => setInvoices(s?.invoices || [])).catch(() => {});
  }, [student.id]);

  async function pay() {
    if (!payForm.amountXof) { setError('Montant obligatoire.'); return; }
    setSaving(true);
    try {
      await api.post('/finance/payments', { studentId: student.id, amountXof: Number(payForm.amountXof), method: payForm.method, reference: payForm.reference });
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{student.nom}</h2>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, fontSize: 12, color: 'var(--muted)' }}>
          <span className="mono">{student.numero}</span>
          <span>·</span><span>{student.classe}</span>
        </div>

        {error && <div className="form-error">{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
          <div style={{ padding: '10px 14px', background: 'var(--surface-soft)', borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Total dû</div>
            <div style={{ fontWeight: 700, fontFamily: 'monospace' }}>{formatXof(student.montantAssigne)}</div>
          </div>
          <div style={{ padding: '10px 14px', background: 'var(--success-soft)', borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Payé</div>
            <div style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--success)' }}>{formatXof(student.totalRegle)}</div>
          </div>
          <div style={{ padding: '10px 14px', background: 'var(--danger-soft)', borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Reste</div>
            <div style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--danger)' }}>{formatXof(student.resteAPayer)}</div>
          </div>
        </div>

        {student.resteAPayer > 0 && (
          <>
            <div className="fieldset-title">Enregistrer un paiement</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div className="field" style={{ margin: 0 }}><label>Montant (XOF)</label>
                <input type="number" value={payForm.amountXof} onChange={(e) => setPayForm({ ...payForm, amountXof: e.target.value })} />
              </div>
              <div className="field" style={{ margin: 0 }}><label>Moyen</label>
                <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
                  <option value="ESPECES">Espèces</option>
                  <option value="VIREMENT">Virement</option>
                  <option value="MOBILE_MONEY">Mobile Money</option>
                  <option value="CHEQUE">Chèque</option>
                </select>
              </div>
              <div className="field" style={{ margin: 0 }}><label>Référence</label>
                <input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} />
              </div>
            </div>
            <button className="btn btn-sm" onClick={pay} disabled={saving} style={{ marginBottom: 14 }}>
              {saving ? 'Enregistrement…' : 'Valider le paiement'}
            </button>
          </>
        )}

        <div className="fieldset-title">Factures</div>
        {invoices.length === 0 ? <div className="empty">Aucune facture.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Libellé</th><th className="num">Montant</th><th className="num">Payé</th><th className="num">Solde</th><th>Statut</th></tr></thead>
            <tbody>{invoices.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.label}</td>
                <td className="num mono">{formatXof(inv.amountXof)}</td>
                <td className="num mono" style={{ color: 'var(--success)' }}>{formatXof(inv.paid || 0)}</td>
                <td className="num mono" style={{ color: (inv.balance || 0) > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatXof(inv.balance || 0)}</td>
                <td><span className={`badge ${inv.status === 'PAYE' ? 'green' : 'gold'}`}>{inv.status}</span></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
